import { useEffect, useState } from 'react';
import apiService from '../services/api';
import RewardPreviewModal from './RewardPreviewModal';

/**
 * How the first-reward ladder looks inside the app.
 *
 * Takes its look from the embed's PointsMilestonesCard (homecrowd-embed
 * src/components/Dashboard/PointsMilestonesCard.js and the .hc-milestones-*
 * rules in its styles.css): 95x107 artwork, 16px Medium title, 14px grey points
 * line, rows divided by a #ebebeb hairline, ordered cheapest-first.
 *
 * Not a literal copy. There is no signed-in student, so the progress bar and the
 * Redeem pill are dropped rather than shown dead, and each tile carries the full
 * field set — type, location, description, what's included, image count — which
 * the app itself hides until a student opens the reward.
 */

// Wider than the app's 402px column: the tiles carry the full detail set here,
// which the app hides behind a tap.
const PREVIEW_WIDTH = 560;

const REWARD_TYPE_LABELS = {
  experience: 'Experience',
  merchandise: 'Merchandise',
  apparel: 'Apparel',
  collectibles: 'Collectibles',
  gift_card: 'Gift Card',
};

function formatPts(value) {
  return `${(Number(value) || 0).toLocaleString('en-US')} pts`;
}

function galleryCount(rung) {
  return Array.isArray(rung.images) ? rung.images.filter(Boolean).length : 0;
}

/** The artwork the app would pick: first gallery image, else the confirmation shot. */
function pickArt(rung) {
  const gallery = Array.isArray(rung.images) ? rung.images.filter(Boolean) : [];
  return gallery[0] || rung.image_url || rung.confirmation_image_url || '';
}

// Each rung opens the same preview the Reward Delivery queue uses, so a reward
// reads identically wherever staff meet it.
function rungDetail(rung) {
  return {
    title: rung.title || 'Reward',
    description: [rung.description, rung.whats_included && `Included: ${rung.whats_included}`]
      .filter(Boolean)
      .join('\n\n'),
    primary_image_url: pickArt(rung),
  };
}

function rungFacts(rung) {
  return [
    ['Points', formatPts(rung.points_cost)],
    ['Type', REWARD_TYPE_LABELS[rung.reward_type] || rung.reward_type],
    ['Location', rung.location],
    ['Status', rung.is_active === false ? 'Inactive — hidden in the app' : 'Active'],
  ];
}

const FirstRewardsPreview = () => {
  const [previewRung, setPreviewRung] = useState(null);
  const [rungs, setRungs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getStaffFirstRewards();
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setRungs(
          list
            .filter(r => Number(r.points_cost) > 0)
            .sort((a, b) => Number(a.points_cost) - Number(b.points_cost))
        );
      } catch (err) {
        if (!cancelled) setError('Could not load the reward ladder.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div style={card}>
        <p style={{ ...muted, margin: 0 }}>Loading preview…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={card}>
        <p style={{ ...muted, margin: 0, color: '#991b1b' }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={card}>
      <h3 style={heading}>In-app preview</h3>
      <p style={{ ...muted, margin: '0 0 20px' }}>
        How the reward ladder appears on the app home screen. Rungs are ordered as they are on
        the school management page.
      </p>

      {rungs.length === 0 ? (
        <p style={{ ...muted, margin: 0 }}>
          No first rewards set up yet, so the home screen shows the points total without a ladder.
        </p>
      ) : (
        <div style={phone}>
          <ul style={list}>
            {rungs.map((rung, index) => {
              const art = pickArt(rung);
              return (
                <li
                  key={rung.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Preview ${rung.title || 'reward'}`}
                  onClick={() => setPreviewRung(rung)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setPreviewRung(rung);
                    }
                  }}
                  style={{
                    ...row,
                    paddingTop: index === 0 ? 0 : 12,
                    paddingBottom: index === rungs.length - 1 ? 0 : 12,
                    borderTop: index === 0 ? 'none' : '1px solid #ebebeb',
                    opacity: rung.is_active === false ? 0.55 : 1,
                    cursor: 'pointer',
                  }}
                >
                  <div style={thumb}>
                    {art ? (
                      <img src={art} alt="" style={thumbImg} />
                    ) : null}
                  </div>
                  <div style={copy}>
                    <span style={title}>{rung.title || 'Reward'}</span>

                    {/* Cost, type, location and gallery size collapsed onto one
                        line — as separate labelled rows they dominated the tile. */}
                    <span style={points}>
                      {[
                        formatPts(rung.points_cost),
                        REWARD_TYPE_LABELS[rung.reward_type] || rung.reward_type,
                        rung.location,
                        galleryCount(rung)
                          ? `${galleryCount(rung)} image${galleryCount(rung) === 1 ? '' : 's'}`
                          : null,
                        rung.is_active === false ? 'Inactive — hidden in the app' : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>

                    {/* Clamped: a long description would otherwise set the tile's
                        height on its own. Full text lives on the edit form. */}
                    {rung.description ? (
                      <span style={clamped} title={rung.description}>
                        {rung.description}
                      </span>
                    ) : null}
                    {rung.whats_included ? (
                      <span style={clamped} title={rung.whats_included}>
                        <strong style={includedLabel}>Included:</strong> {rung.whats_included}
                      </span>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {previewRung && (
        <RewardPreviewModal
          title={previewRung.title || 'Reward'}
          detail={rungDetail(previewRung)}
          facts={rungFacts(previewRung)}
          onClose={() => setPreviewRung(null)}
        />
      )}
    </div>
  );
};

const card = {
  backgroundColor: 'white',
  borderRadius: 12,
  padding: 32,
  marginBottom: 24,
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
};

const heading = {
  fontFamily: '"Baikal Book"',
  fontSize: 20,
  fontWeight: 600,
  color: '#111827',
  marginBottom: 8,
};

const muted = {
  fontFamily: '"Baikal Book"',
  fontSize: 13,
  color: '#6b7280',
  lineHeight: 1.5,
};

const phone = {
  width: PREVIEW_WIDTH,
  maxWidth: '100%',
  border: '1px solid #ebebeb',
  borderRadius: 16,
  padding: 20,
  background: 'white',
};

const list = { listStyle: 'none', margin: 0, padding: 0 };

// Top-aligned, not centred like the app's row: the detail block makes these tiles
// much taller than the artwork.
const row = { display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 14 };

const thumb = {
  width: 68,
  height: 76,
  borderRadius: 12,
  background: '#ebebeb',
  overflow: 'hidden',
  flexShrink: 0,
};

const thumbImg = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' };

const copy = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 3,
};

const title = {
  fontSize: 15,
  fontFamily: '"Baikal Medium", "Baikal Book"',
  fontWeight: 500,
  color: '#000',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const points = {
  fontSize: 13,
  fontFamily: '"Baikal Book"',
  color: '#888',
  lineHeight: 1.4,
};

// Two lines max, so one verbose reward cannot stretch the whole list.
const clamped = {
  fontFamily: '"Baikal Book"',
  fontSize: 13,
  lineHeight: 1.45,
  color: '#374151',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const includedLabel = { fontWeight: 600, color: '#6b7280' };

export default FirstRewardsPreview;
